import Metric, { MetricSize } from '@customer/components/data-display/metric/Metric';
import InsightsCard from '@customer/components/data-display/insights-card/InsightsCard';
import styles from './SelfTestOverview.module.scss';
import { useTranslation } from 'react-i18next';
import { useLocaleFormatter } from '@customer/shared/hooks/useLocaleFormatter';
import {
  HealthSummaryQueryParams,
  useGetHealthSiteOverviewQuery,
} from '@customer/store/apis/healthApi';
import { useAppSelector } from '@customer/store/hooks';
import SparkLine from '@customer/components/charts/spark-line/SparkLine';
import { FaultLevel } from '@customer/store/slices/globalSlice';
import { ChartColorScheme } from '@customer/components/charts/config';
import { ReactElement } from 'react';
import { FilterGroup } from '@customer/filters/types';
import useFilters from '@customer/filters/useFilters';

export const SelfTestOverview = () => {
  const { t } = useTranslation();
  const { globalTimeRange } = useLocaleFormatter();

  const siteId = useAppSelector(state => state.auth.siteId);
  const timeRange = useAppSelector(state => state.global.timeRange);
  const filters = useFilters<HealthSummaryQueryParams>(FilterGroup.HealthSelfTest);

  const { data: overviewData } = useGetHealthSiteOverviewQuery(
    {
      query: {
        ...filters,
        timeRange,
      },
    },
    { refetchOnMountOrArgChange: 60, skip: typeof siteId === 'undefined' },
  );

  interface SectionConfig {
    faultLevel: FaultLevel;
    assetsFaultTotal?: number;
    assetsFaultActive?: number;
    colorScheme: ChartColorScheme;
  }

  const sectionConfigs: SectionConfig[] = [
    {
      faultLevel: FaultLevel.One,
      assetsFaultTotal: overviewData?.assetsL1FaultTotal,
      assetsFaultActive: overviewData?.assetsL1FaultActive,
      colorScheme: ChartColorScheme.RedGrey,
    },
    {
      faultLevel: FaultLevel.Two,
      assetsFaultTotal: overviewData?.assetsL2FaultTotal,
      assetsFaultActive: overviewData?.assetsL2FaultActive,
      colorScheme: ChartColorScheme.OrangeGrey,
    },
  ];

  const sections: ReactElement[] = sectionConfigs.map(
    ({ faultLevel, assetsFaultTotal, assetsFaultActive, colorScheme }: SectionConfig) => (
      <div key={faultLevel} className={styles.insightSection}>
        <div className={styles.spark}>
          <div className={styles.spark__title}>
            {t('AssetsWithLevelFaults', { level: faultLevel })}
          </div>
          <SparkLine
            faultLevel={faultLevel}
            colorScheme={colorScheme}
            getCategoryLabel={(label: string) => label}
          />
        </div>

        <Metric
          dataPrimary={assetsFaultTotal}
          dataSecondary={overviewData?.totalAssets}
          label={
            <>
              {globalTimeRange}
              <br />
              {t('Total')}
            </>
          }
          size={MetricSize.XL}
          isFraction
        />
        <Metric dataPrimary={assetsFaultActive} label={t('CurrentlyActive')} size={MetricSize.XL} />
      </div>
    ),
  );

  return <InsightsCard cols={2} sections={sections} />;
};

export default SelfTestOverview;
